import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { ArrowRight, ShieldCheck, Zap, Star } from "lucide-react";
import LogoAtivaJa from "@/components/LogoAtivaJa";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [bloqueio, setBloqueio] = useState(null);
  const [fp, setFp] = useState(false);
  const [fpEmail, setFpEmail] = useState("");
  const [fpCodigo, setFpCodigo] = useState("");
  const [fpSenha, setFpSenha] = useState("");
  const [fpEtapa, setFpEtapa] = useState(1);
  const [fpBusy, setFpBusy] = useState(false);

  const fpSolicitar = async () => {
    if (!fpEmail.trim()) { toast.error("Informe seu e-mail"); return; }
    setFpBusy(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/esqueci-senha`, { email: fpEmail.trim() });
      toast.success(r.data.mensagem);
      setFpEtapa(2);
    } catch (err) { toast.error(formatApiError(err)); } finally { setFpBusy(false); }
  };

  const fpRedefinir = async () => {
    setFpBusy(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/redefinir-senha`,
        { email: fpEmail.trim(), codigo: fpCodigo.trim(), nova_senha: fpSenha });
      toast.success(r.data.mensagem);
      setFp(false); setFpEtapa(1); setFpCodigo(""); setFpSenha("");
      setEmail(fpEmail.trim());
    } catch (err) { toast.error(formatApiError(err)); } finally { setFpBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    sessionStorage.setItem("mj_splash_pending", "1");
    try {
      const u = await login(email.trim(), password);
      toast.success(`Bem-vindo, ${u.name || u.email}`);
      if (u.role === "montador") {
        navigate(u.primeiro_acesso ? "/montador/contrato" : "/montador");
      } else {
        navigate("/admin");
      }
    } catch (err) {
      sessionStorage.removeItem("mj_splash_pending");
      const msg = formatApiError(err);
      const m = String(msg).match(/https?:\/\/\S*\/regularizar\/[\w-]+/);
      if (m) {
        const fid = m[0].split("/regularizar/")[1];
        setBloqueio({ msg: String(msg).split("Regularize")[0].trim(), link: `/regularizar/${fid}` });
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white font-body text-aj-text">
      {/* Lado esquerdo — Marca + Valor */}
      <div className="hidden lg:flex bg-gradient-to-br from-aj-blue-deep via-aj-blue-dark to-aj-blue text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-aj-orange/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-20 w-96 h-96 rounded-full bg-aj-amber/15 blur-3xl pointer-events-none" />

        <Link to="/" className="relative z-10" data-testid="login-logo-link">
          <div className="bg-white inline-block p-3 rounded-lg">
            <LogoAtivaJa size="lg" />
          </div>
        </Link>

        <div className="relative z-10 aj-fade-up">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-aj-amber mb-4">
            Plataforma Operacional de Montagem
          </div>
          <h2 className="font-display font-extrabold text-3xl lg:text-4xl leading-tight">
            Conectando empresas, montadoras<br /> e montadores.
          </h2>
          <p className="mt-5 text-sm text-white/75 max-w-md leading-relaxed">
            Operação completa. Auditoria imutável. Indicadores em tempo real.
            Tudo o que sua equipe precisa para entregar montagens com excelência.
          </p>
          <ul className="mt-8 space-y-3 text-sm">
            <FeaturePill icon={<Zap className="w-4 h-4" />} text="Agendamento automático pós-entrega" />
            <FeaturePill icon={<ShieldCheck className="w-4 h-4" />} text="Compliance jurídico e LGPD" />
            <FeaturePill icon={<Star className="w-4 h-4" />} text="Score regional inteligente" />
          </ul>
        </div>

        <div className="relative z-10 text-xs text-white/40 font-mono">v1.0 · Brasil</div>
      </div>

      {/* Lado direito — Form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-md space-y-7" data-testid="form-login">
          {bloqueio && (
            <div className="border border-[#E60000]/50 bg-[#FFF5F5] p-4 text-sm space-y-2" data-testid="login-bloqueio-financeiro">
              <div className="font-bold text-[#B91C1C]">⚠ Conta bloqueada por pendência financeira</div>
              <p className="text-xs">{bloqueio.msg} O acesso de toda a equipe é liberado imediatamente após a confirmação do pagamento, sem perda de dados.</p>
              <a href={bloqueio.link} className="inline-block px-4 py-2 bg-[#E60000] text-white text-xs font-bold uppercase tracking-wider"
                data-testid="login-regularizar-link">Regularizar agora</a>
            </div>
          )}
          <Link to="/" className="lg:hidden inline-flex items-center" data-testid="login-logo-mobile">
            <LogoAtivaJa size="md" />
          </Link>
          <div>
            <div className="aj-eyebrow mb-3">Acesso ao painel</div>
            <h1 className="font-display font-extrabold text-3xl lg:text-4xl text-aj-blue-deep leading-tight">
              Entrar no <span className="aj-accent-orange">MontaJá</span>
            </h1>
            <p className="text-sm text-aj-text-muted mt-3">
              Admin, Empresa, Montadora ou Montador.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-aj-text-muted" htmlFor="email">E-mail</Label>
              <Input id="email" type="email" value={email} required
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="mt-2 h-12 text-base"
                data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-aj-text-muted" htmlFor="password">Senha</Label>
                <button type="button" onClick={() => { setFp(true); setFpEmail(email); }}
                  className="text-xs text-aj-orange hover:underline" data-testid="login-forgot">Esqueci minha senha</button>
              </div>
              <Input id="password" type="password" value={password} required
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-12 text-base"
                data-testid="login-password-input" />
            </div>
          </div>

          <button type="submit" disabled={loading}
            className="btn-aj-primary w-full justify-center h-12 text-base disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="login-submit-button">
            {loading ? "Entrando…" : (<>Entrar <ArrowRight className="w-4 h-4" /></>)}
          </button>

          <div className="text-xs text-aj-text-muted text-center pt-5 border-t border-aj-border">
            Ainda não tem conta?{" "}
            <Link to="/cadastro" className="text-aj-orange font-semibold hover:underline" data-testid="login-cadastre-se">
              Cadastre-se aqui
            </Link>
            <span className="text-aj-text-muted"> — Montador, Montadora ou Empresa</span>
          </div>
        </form>
      </div>

      <Dialog open={fp} onOpenChange={o => { setFp(o); if (!o) setFpEtapa(1); }}>
        <DialogContent className="max-w-sm" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Recuperar senha</DialogTitle>
          </DialogHeader>
          {fpEtapa === 1 ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Informe seu e-mail. Enviaremos um código de 6 dígitos para o <strong>WhatsApp cadastrado</strong> na sua conta.
              </p>
              <Input type="email" placeholder="seu@email.com" value={fpEmail}
                onChange={e => setFpEmail(e.target.value)} data-testid="forgot-email-input" />
              <Button onClick={fpSolicitar} disabled={fpBusy} className="w-full btn-sharp bg-[#FF6700] hover:bg-[#e55d00] text-white"
                data-testid="forgot-enviar-btn">{fpBusy ? "Enviando..." : "Enviar código"}</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Digite o código de 6 dígitos recebido no WhatsApp e escolha a nova senha (vale por 15 minutos).
              </p>
              <Input placeholder="Código de 6 dígitos" value={fpCodigo} maxLength={6}
                onChange={e => setFpCodigo(e.target.value.replace(/\D/g, ""))} data-testid="forgot-codigo-input" />
              <Input type="password" placeholder="Nova senha (mín. 6 caracteres)" value={fpSenha}
                onChange={e => setFpSenha(e.target.value)} data-testid="forgot-senha-input" />
              <Button onClick={fpRedefinir} disabled={fpBusy || fpCodigo.length !== 6 || fpSenha.length < 6}
                className="w-full btn-sharp bg-[#142766] hover:bg-[#0e1c4d] text-white"
                data-testid="forgot-redefinir-btn">{fpBusy ? "Salvando..." : "Redefinir senha"}</Button>
              <button type="button" onClick={fpSolicitar} className="text-xs underline text-muted-foreground w-full"
                data-testid="forgot-reenviar">Reenviar código</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeaturePill({ icon, text }) {
  return (
    <li className="flex items-center gap-3">
      <span className="w-8 h-8 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center text-aj-amber">
        {icon}
      </span>
      <span className="text-white/90">{text}</span>
    </li>
  );
}
